import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiDelete, apiGet, apiPost, apiPut } from "../api/client";
import {
  OfferBadge,
  SecondaryButton,
  Stars,
  VisitAnswerChips,
  VisitForm,
  fmtVisitDate,
  type Offer,
  type Visit,
  type VisitDraft,
} from "../components/VisitForm";

interface VisitsResponse {
  visits: Visit[];
  count: number;
  offers_without_visits: Offer[];
}

interface ListingOption {
  listing_id: string;
  address?: string;
  town?: string;
  status?: string;
}

export default function Visits() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Visit | null>(null);
  const [adding, setAdding] = useState(false);

  const visitsQ = useQuery<VisitsResponse>({
    queryKey: ["visits"],
    queryFn: () => apiGet("/api/visits"),
  });

  // The picker needs every cached listing, not just active ones — you often
  // write up a showing after the house has gone pending.
  const listingsQ = useQuery<{ listings: ListingOption[] }>({
    queryKey: ["listings-for-visits"],
    queryFn: () => apiGet("/api/listings?status=all"),
  });

  const listingOptions = useMemo(() => {
    const rows = listingsQ.data?.listings ?? [];
    return [...rows].sort((a, b) => (a.address ?? "").localeCompare(b.address ?? ""));
  }, [listingsQ.data]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["visits"] });

  const create = useMutation({
    mutationFn: (draft: VisitDraft) => apiPost<{ visit: Visit }>("/api/visits", draft),
    onSuccess: () => {
      setAdding(false);
      invalidate();
    },
  });

  const update = useMutation({
    mutationFn: ({ visitId, draft }: { visitId: string; draft: VisitDraft }) =>
      apiPut<{ visit: Visit }>(`/api/visits/${visitId}`, draft),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (visitId: string) => apiDelete(`/api/visits/${visitId}`),
    onSuccess: invalidate,
  });

  const visits = visitsQ.data?.visits ?? [];
  const orphanOffers = visitsQ.data?.offers_without_visits ?? [];
  const uniqueHomes = new Set(visits.map((v) => v.listing_id)).size;
  const offerCount =
    new Set(visits.filter((v) => v.offer).map((v) => v.listing_id)).size + orphanOffers.length;

  return (
    <>
      <div className="row" style={{ marginBottom: 12 }}>
        <h1 style={{ margin: 0, flex: 1 }}>Visits</h1>
        <button onClick={() => { setAdding((v) => !v); setEditing(null); }}>
          {adding ? "Cancel" : "Log a visit"}
        </button>
      </div>

      {adding && (
        <div className="panel">
          <h3 style={{ marginTop: 0 }}>Log a visit</h3>
          <VisitForm
            listings={listingOptions}
            submitting={create.isPending}
            error={create.error ? String(create.error) : null}
            onSubmit={(draft) => create.mutate(draft)}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 12 }}>
        <StatTile label="Homes seen" value={uniqueHomes} loading={visitsQ.isLoading} />
        <StatTile label="Total showings" value={visits.length} loading={visitsQ.isLoading} />
        <StatTile label="Offers made" value={offerCount} loading={visitsQ.isLoading} />
      </div>

      {visitsQ.isLoading && <p className="muted">Loading…</p>}
      {visitsQ.error && <p className="tag-error">Failed to load visits.</p>}
      {!visitsQ.isLoading && visits.length === 0 && orphanOffers.length === 0 && (
        <div className="panel">
          <p className="muted" style={{ margin: 0 }}>
            No visits logged yet. After a showing, click <strong>Log a visit</strong> to record what
            you thought while it's still fresh — notes are the one thing here that can't be
            re-scraped later.
          </p>
        </div>
      )}

      {visits.map((visit) =>
        editing?.visit_id === visit.visit_id ? (
          <div className="panel" key={visit.visit_id}>
            <h3 style={{ marginTop: 0 }}>Edit visit — {visit.listing?.address ?? visit.listing_id}</h3>
            <VisitForm
              listings={listingOptions}
              initial={visit}
              submitting={update.isPending}
              error={update.error ? String(update.error) : null}
              onSubmit={(draft) => update.mutate({ visitId: visit.visit_id, draft })}
              onCancel={() => setEditing(null)}
            />
          </div>
        ) : (
          <VisitCard
            key={visit.visit_id}
            visit={visit}
            onEdit={() => { setEditing(visit); setAdding(false); }}
            onDelete={() => remove.mutate(visit.visit_id)}
            deleting={remove.isPending && remove.variables === visit.visit_id}
          />
        ),
      )}

      {orphanOffers.length > 0 && (
        <div className="panel">
          <h3 style={{ marginTop: 0 }}>Offers without a logged visit</h3>
          {orphanOffers.map((offer) => (
            <div
              key={offer.listing_id}
              className="row"
              style={{ borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 10 }}
            >
              <Link to={`/listings/${offer.listing_id}`} style={{ flex: 1 }}>
                {offer.listing?.address ?? offer.listing_id}
              </Link>
              <span className="muted" style={{ fontSize: 12 }}>
                {fmtVisitDate(offer.offered_on)}
              </span>
              <OfferBadge offer={offer} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function StatTile({ label, value, loading }: { label: string; value: number; loading: boolean }) {
  return (
    <div className="panel" style={{ marginBottom: 0, padding: 14 }}>
      <div className="muted" style={{ fontSize: 12 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 600, marginTop: 4 }}>{loading ? "…" : value}</div>
    </div>
  );
}

function VisitCard({
  visit,
  onEdit,
  onDelete,
  deleting,
}: {
  visit: Visit;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const listing = visit.listing;
  const thumb = listing?.thumbnail;

  return (
    <div className="panel">
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        {thumb && (
          <img
            src={thumb.startsWith("http") ? thumb : `/${thumb}`}
            alt=""
            style={{ width: 132, height: 99, objectFit: "cover", borderRadius: 6, flexShrink: 0 }}
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row" style={{ gap: 8 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>
              <Link to={`/listings/${visit.listing_id}`}>
                {listing?.address ?? visit.listing_id}
              </Link>
            </div>
            {visit.offer && <OfferBadge offer={visit.offer} />}
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            Visited {fmtVisitDate(visit.visited_on)}
            {listing?.town ? ` · ${listing.town}` : ""}
            {listing?.list_price != null ? ` · $${listing.list_price.toLocaleString()}` : ""}
          </div>
          {visit.rating != null && (
            <div style={{ marginTop: 6 }}>
              <Stars value={visit.rating} />
            </div>
          )}
          <VisitAnswerChips visit={visit} />
          {visit.notes && (
            <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.5, marginTop: 10, marginBottom: 0 }}>
              {visit.notes}
            </p>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
          <button onClick={onEdit} style={{ padding: "6px 12px" }}>
            Edit
          </button>
          {confirming ? (
            <>
              <SecondaryButton onClick={onDelete} danger disabled={deleting}>
                {deleting ? "…" : "Confirm"}
              </SecondaryButton>
              <SecondaryButton onClick={() => setConfirming(false)}>Keep</SecondaryButton>
            </>
          ) : (
            <SecondaryButton onClick={() => setConfirming(true)}>Delete</SecondaryButton>
          )}
        </div>
      </div>
    </div>
  );
}
